import {
  AfterViewInit,
  Component, computed, effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  viewChild
} from '@angular/core';
import { NgxsModule, Store } from '@ngxs/store';
import {
  AuthState,
  BranchChange,
  loadMyBranches,
  Logout,
  NotificationClear,
  NotificationIncrease
} from '@beautyapp/auth';
import { BehaviorSubject, Subject } from 'rxjs';
import { NavigationItem } from '../common/navigation';
import {
  BookingService,
  Branch,
  CashierState,
  ChangeLanguage,
  CurrentCashier,
  CustomerImportService,
  ENV_CONFIG,
  EnvironmentConfig,
  getEmployees,
  ResizeService,
  UserService,
  ClassificationCodeService,
  OnlineBookingState,
  WebSocketConnect,
  WebSocketDisconnect,
  NpsService
} from '@beautyapp/core';
import { takeUntil, tap } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SwPush } from '@angular/service-worker';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NzDrawerModule, NzDrawerService } from 'ng-zorro-antd/drawer';
import { NotificationPanelComponent } from '../components/notification-panel/notification-panel.component';
import { NzNotificationComponent, NzNotificationService } from 'ng-zorro-antd/notification';
import { OnlineBookingModalComponent, NpsSurveyComponent } from '@beautyapp/shared';
import { NzModalService } from 'ng-zorro-antd/modal';
import packageInfo from '../../../../../package.json';
import { ImportantInfoComponent } from '../components/important-info/important-info.component';
import { navigation } from './navigation';
import { shapeNavigation } from './shape-navigation';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { FormsModule } from '@angular/forms';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzFlexDirective } from 'ng-zorro-antd/flex';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { AppointmentSearchComponent } from '@beautyapp/appointment';

@Component({
  selector: 'beautyapp-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    NzGridModule,
    NzLayoutModule,
    NzButtonModule,
    NzDropDownModule,
    NzIconModule,
    NgxPermissionsModule,
    TranslateModule,
    NzSelectModule,
    FormsModule,
    NzSpaceModule,
    NzAlertModule,
    NgxsModule,
    NzBadgeModule,
    NzTooltipDirective,
    NzFlexDirective,
    NzProgressModule,
    AppointmentSearchComponent,
    NzDrawerModule
  ]
})
export class MainComponent implements OnInit, OnDestroy, AfterViewInit {
  public config: EnvironmentConfig = inject(ENV_CONFIG);
  private store: Store = inject(Store);
  private translateService: TranslateService = inject(TranslateService);
  private resizeSvc: ResizeService = inject(ResizeService);
  private bookingService: BookingService = inject(BookingService);
  private userService: UserService = inject(UserService);
  private deviceService: DeviceDetectorService = inject(DeviceDetectorService);
  private drawerService: NzDrawerService = inject(NzDrawerService);
  private swPush: SwPush = inject(SwPush);
  private notificationService: NzNotificationService = inject(NzNotificationService);
  private modalService: NzModalService = inject(NzModalService);
  private classificationCodeService = inject(ClassificationCodeService);
  private customerImportService = inject(CustomerImportService);
  private npsService = inject(NpsService);

  importState$ = this.customerImportService.state$;
  version = packageInfo.version;


  notificationBtnTpl = viewChild<TemplateRef<{ $implicit: NzNotificationComponent }>>('notificationBtnTpl');


  selectedBranch$ = this.store.select(AuthState.getActiveBranch);
  branches$ = this.store.select(AuthState.myBranches);
  notificationCount$ = this.store.select(AuthState.getNotificationCount);

  authUser = this.store.selectSnapshot(AuthState.getProfile);

  activeBranch = this.store.selectSignal((state) => state.auth.activeBranch);
  ehrActive = computed(() => this.activeBranch().ehrEnabled);

  isMobile = this.deviceService.isMobile();

  navigation: NavigationItem[] = [];
  selectedBranch!: Branch;
  isCollapsed = true;
  sub$: Subject<any> = new Subject<any>();

  size = 80;
  lang: any = 'ka';

  deviceInfo: any = null;

  cashiersCurrentPending$ = this.store.select(
    CashierState.currentCashierIsPending
  );

  bookings = [];

  // private readonly _messaging = inject(Messaging);
  private readonly _message = new BehaviorSubject<unknown | undefined | any>(undefined);

  message$ = this._message.asObservable();

  @HostListener('window:resize', [])
  private onResize() {
    this.detectScreenSize();
  }

  constructor() {
    this.lang = this.translateService.currentLang;
    // this.epicFunction();

    effect(() => {
      if (this.activeBranch()) {
        this.navigation = shapeNavigation(navigation, this.activeBranch());
      }
    });
  }

  async ngOnInit() {

    this.store.dispatch(new loadMyBranches());
    this.store.dispatch(new getEmployees());
    this.selectedBranch$.pipe(takeUntil(this.sub$)).subscribe((res) => {
      if (res) {
        this.selectedBranch = res;
      }
    });
    this.store.dispatch(new CurrentCashier());
    // if(this.swUpdate.isEnabled) {
    //   this.swUpdate.available.subscribe(() => {
    //     if (confirm("New version available. Load New Version?")) {
    //       window.location.reload();
    //     }
    //   });
    // }

    this.bookingService.onlineBookings().subscribe((res) => {
      this.bookings = res;
    });

    // Connect to WebSocket for real-time notifications
    this.connectWebSocket();
    this.listenForWebSocketNotifications();

    // this._getDeviceToken();
    // this._onMessage();
    // this.listenForNotifications();
    this.listenForMessages();

    this.showNpsSurvey();
    // this.epicFunction();
  }

  private showNpsSurvey() {
    this.npsService.check().subscribe({
      next: (res) => {
        if (!res.completed && res.activeConfig) {
          this.openNpsModal(res.activeConfig.question);
        }
      },
      error: () => {
        // If check fails, don't show the modal
      }
    });
  }

  private openNpsModal(customQuestion?: string) {
    this.modalService.create({
      nzTitle: this.translateService.instant('nps.title'),
      nzContent: NpsSurveyComponent,
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzFooter: null,
      nzWidth: '500px',
      nzData: { customQuestion }
    });
  }

  alertPlay() {
    const audio: HTMLAudioElement = new Audio('/assets/toast_sound.mp3');
    audio.play();
  }

  /**
   * Connect to WebSocket server for real-time notifications using NGXS
   */
  private connectWebSocket(): void {
    const user = this.store.selectSnapshot(AuthState.getProfile);
    const branch = this.store.selectSnapshot(AuthState.getActiveBranch);
    const organization = this.store.selectSnapshot(AuthState.getOrganization);

    if (user && branch) {
      this.store.dispatch(new WebSocketConnect({
        userId: user.id,
        branchId: branch.id,
        organizationId: organization?.id,
        socketUrl: this.config.socketUrl
      }));
    }
  }

  /**
   * Listen for WebSocket notifications from NGXS state and show toasts
   */
  private listenForWebSocketNotifications(): void {
    this.store.select(OnlineBookingState.getLastNotification)
      .pipe(takeUntil(this.sub$))
      .subscribe(notification => {
        if (!notification) return;

        console.log('[MainComponent] Online booking notification received from NGXS:', notification);

        // Increment notification count
        this.store.dispatch(new NotificationIncrease());

        // Play notification sound
        this.alertPlay();

        // Show notification toast
        const title = notification.type === 'offer_order_booking'
          ? 'ახალი ჩაწერა შეთავაზებით'
          : 'ახალი ონლაინ ჩაწერა';

        const content = notification.type === 'offer_order_booking'
          ? `${notification.newBookingsCount || 1} ახალი სერვისი მოელოდება დადასტურებას`
          : 'ახალი ჩაწერა მოელოდება დადასტურებას';

        this.notificationService.create(
          'info',
          title,
          content,
          {
            nzDuration: 5000,
            nzButton: this.notificationBtnTpl()
          }
        ).onClick.subscribe(() => {
          // Open notification panel when toast is clicked
          this.openNotification();
        });
      });
  }

  listenForMessages() {
    this.swPush.messages.subscribe((message: any) => {
      const { title, body, data } = message.notification;
      // Here you can display the notification or process the payload
      this.store.dispatch(new NotificationIncrease());
      this.notificationService.create(
        'info',
        title,
        body,
        {
          nzDuration: 0, // This makes the notification stay indefinitely
          nzButton: this.notificationBtnTpl()
        }
      ).onClose.subscribe(() => {
        if (data.type === 'onlineBooking') {
          const modal = this.modalService.create({
            nzClosable: false,
            nzMaskClosable: false,
            nzTitle: this.translateService.instant('calendar.planVisit'),
            nzContent: OnlineBookingModalComponent,
            nzWidth: '1100px',
            nzStyle: { top: '0px' },
            nzBodyStyle: { padding: '0' },
            nzData: {
              id: data.id
            }
          });
        }
      });
      this.alertPlay();
    });
  }

  listenForNotifications(): void {
    const channel = new BroadcastChannel('notification_channel');
    channel.onmessage = (event) => {
      if (event.data.type === 'INCREMENT_NOTIFICATION_COUNT') {
        this.store.dispatch(new NotificationIncrease());

        this.alertPlay();
      }
    };
  }

  // private _getDeviceToken(): void {
  //   getToken(this._messaging, { vapidKey: this.config.vapidKey })
  //     .then((token: any) => {
  //       console.log(token);
  //       // save the token in the server, or do whathever you want
  //
  //       this.userService.pushFirebaseToken({
  //         token
  //       })
  //         .pipe(
  //           takeUntil(this.sub$)
  //         )
  //         .subscribe();
  //     })
  //     .catch((error: any) => console.log('Token error', error));
  // }
  //
  // private _onMessage(): void {
  //   onMessage(this._messaging, {
  //     next: () => {
  //       this.store.dispatch(new NotificationIncrease());
  //       this.alertPlay();
  //     },
  //     error: (error: any) => console.log('Message error', error),
  //     complete: () => console.log('Done listening to messages')
  //   });
  //
  // }


  epicFunction() {
    this.deviceInfo = this.deviceService.getDeviceInfo();
    this.isMobile = this.deviceService.isMobile();
    if (this.isMobile) {
      // this.size = 0;
      // this.isCollapsed = true;
    }
  }

  ngAfterViewInit() {
    this.detectScreenSize();
  }

  private detectScreenSize() {
    this.resizeSvc.init();
    this.resizeSvc.mediaBreakpoint$.pipe(
      tap((r) => {
        this.size = r;
        if (r === 0) {
          this.isCollapsed = true;
        }
      })
    );
  }

  logout() {
    this.store.dispatch(new Logout());
  }

  setBranch(branch: any) {
    this.selectedBranch = branch;
    this.store.dispatch(new BranchChange(branch));
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
    // Disconnect WebSocket when component is destroyed
    this.store.dispatch(new WebSocketDisconnect());
  }

  langChange(lang: 'en' | 'ru' | 'ka') {
    this.store
      .dispatch(new ChangeLanguage(lang))
      .pipe()
      .subscribe(() => {
        window.location.reload();
      });
  }

  versionUpdate() {
    window.location.reload();
  }

  openNotification() {
    this.drawerService.create({
      nzContent: NotificationPanelComponent,
      nzPlacement: 'right',
      nzWidth: 700
    });

    this.store.dispatch(new NotificationClear());
  }

  openImportantInfo() {
    this.modalService.create({
      nzContent: ImportantInfoComponent,
      nzFooter: null,
      nzWidth: '90%'
    });
  }
}
